import { type PricingConfig } from 'hooks/billing/usePricingConfig';
import {
  ALERT_TYPES,
  type AccountTier,
  BLOCKED_REASONS,
  type QuotaResponse,
} from 'types/billing';
import { isPaidTier } from 'utils/billing';
import { deriveAlert } from './deriveAlert';
import { trialDayBand, type TrialInfo } from './trialDayBand';

/** DS Badge variant used to color the status pill by severity. */
export type BillingStatusVariant = 'emphasis' | 'warning' | 'danger';

/** Value-const for the {@link BillingStatusVariant} union. */
export const BILLING_STATUS_VARIANTS = {
  emphasis: 'emphasis',
  warning: 'warning',
  danger: 'danger',
} as const satisfies Record<string, BillingStatusVariant>;

/** i18n key suffixes under `billing.pill.`, one per status-pill state. */
export const BILLING_STATUS_KEYS = {
  trial: 'trial',
  migration: 'migration',
  migrationLastDay: 'migration-last-day',
  trialEnded: 'trial-ended',
  healthy: 'healthy',
  approaching: 'approaching',
  overage: 'overage',
  blocked: 'blocked',
} as const;

export type BillingStatusKey =
  (typeof BILLING_STATUS_KEYS)[keyof typeof BILLING_STATUS_KEYS];

/** i18n key suffixes under `billing.pill.cta.` — the inline pill call-to-action. */
export const BILLING_STATUS_CTAS = {
  choosePlan: 'choose-plan',
  seePlan: 'see-plan',
  upgradePlan: 'upgrade-plan',
  movePlan: 'move-plan',
} as const;

export type BillingStatusCta =
  (typeof BILLING_STATUS_CTAS)[keyof typeof BILLING_STATUS_CTAS];

export interface BillingStatusVM {
  /** i18n key suffix under `billing.pill.` */
  key: BillingStatusKey;
  /** Values are numbers when they drive i18next pluralization (`count`). */
  interpolation: Record<string, string | number> | undefined;
  variant: BillingStatusVariant;
  /** Inline CTA shown in the tag (Figma: "· Select a plan ›"). */
  cta: BillingStatusCta;
}

/**
 * Maps a quota snapshot to the top-bar status-pill view-model — the single
 * always-on surface for every billing state (trial / healthy / approaching /
 * overage / blocked). Reuses the {@link deriveAlert} state machine; the only
 * addition is the "healthy" case (no alert + an active plan), which the
 * banner intentionally omits.
 *
 * Pure — no gate/quota fetching (that's the hook's job).
 *
 * @param quota - Current quota snapshot from the billing API.
 * @param cfg - Pricing config supplying thresholds (e.g. approaching limit).
 * @param trial - Active trial info from account-info, if present.
 * @param tier - Account tier from account-info, if present.
 * @returns The status-pill view-model, or `undefined` when there is nothing
 * meaningful to show (e.g. no plan and no alert).
 */
export const deriveBillingStatus = (
  quota: QuotaResponse,
  cfg: PricingConfig,
  trial?: TrialInfo,
  tier?: AccountTier,
): BillingStatusVM | undefined => {
  const alert = deriveAlert(quota, cfg);

  if (alert?.type === ALERT_TYPES.blocked) {
    // trial-end gets its own "Trial ended" copy (matrix); other reasons share
    // the generic red "Blocked" tag.
    return {
      key:
        quota.blocked?.reason === BLOCKED_REASONS.trialEnd
          ? BILLING_STATUS_KEYS.trialEnded
          : BILLING_STATUS_KEYS.blocked,
      interpolation: undefined,
      variant: 'danger',
      cta: BILLING_STATUS_CTAS.seePlan,
    };
  }

  // Trial state is read from /account-info (D22 — /quota no longer carries
  // trial), the SAME source the sidebar widget uses (deriveSidebarWidget), so
  // the top-bar pill and the sidebar trial widget stay consistent (PLT-4152).
  // The ending band uses the shared trialDayBand thresholds; the pill turns red
  // in the ending band per the matrix and its CTA flips "Choose a plan" →
  // "Browse plans". Suppressed on tier==='paid': /account-info can lag
  // trial.active=true after conversion, so paid accounts get no *trial* pill
  // here — their overage/approaching/blocked pills still surface below (PLT-4159).
  if (trial?.active && !isPaidTier(tier)) {
    const ending =
      trialDayBand(trial, cfg.trialEndingThresholdDays) === 'ending';

    return {
      key: BILLING_STATUS_KEYS.trial,
      // `count` (number) so i18next picks "1 day left" vs "N days left".
      interpolation: { count: trial.daysRemaining },
      variant: ending ? 'danger' : 'emphasis',
      cta: ending
        ? BILLING_STATUS_CTAS.seePlan
        : BILLING_STATUS_CTAS.choosePlan,
    };
  }

  if (alert?.type === ALERT_TYPES.overageActive && quota.overage) {
    // Overage is its own amber/warning treatment (Figma 3397:8984) — an ongoing
    // "you're paying on-demand" notice, distinct from the red hard-block states.
    return {
      key: BILLING_STATUS_KEYS.overage,
      interpolation: { credits: quota.overage.credits.toLocaleString() },
      variant: 'warning',
      cta: BILLING_STATUS_CTAS.upgradePlan,
    };
  }

  if (!quota.plan) return undefined;

  if (alert?.type === ALERT_TYPES.percentageThreshold) {
    return {
      key: BILLING_STATUS_KEYS.approaching,
      interpolation: {
        remaining: quota.plan.credits_remaining.toLocaleString(),
        granted: quota.plan.credits_granted.toLocaleString(),
      },
      // Approaching the pool limit reads as the same red "act now" signal as a
      // trial ending (not the amber overage notice) — danger across all 3
      // surfaces.
      variant: 'danger',
      cta: BILLING_STATUS_CTAS.upgradePlan,
    };
  }

  // Healthy/paid: the design hides the tag entirely (matrix). No alert + a plan
  // present → nothing to surface.
  return undefined;
};
